import cn from 'classnames';
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { buildTemplatesUrl } from '@/shared/lib';
import { Button, Input, Select, Textarea, toastCaller } from '@/shared/ui';
import { ValueType } from '@/shared/ui/Select/Select.types';

import { ModalID, modalModel } from '@/entities/modal';
import {
  DocumentInclusion,
  DocumentType,
  EncounterSchema,
  getPathWithSearch,
  isProtocolTemplate,
  MAX_ALWAYS_DOCUMENTS,
  resolveTemplatesBackPath,
  SchemaDocumentRef,
  templatesModel,
  VersionStatus,
} from '@/entities/templates';

import { templateEditorMessages } from '@/features/templateEditor/config/i18n';
import { generateCodeFromName } from '@/features/templateEditor/lib/codeGenerator';
import { templateListMessages } from '@/features/templateList';

import { encounterSchemaMessages } from '../../config/i18n';
import { AddSchemaDocumentDialog } from '../AddSchemaDocumentDialog/AddSchemaDocumentDialog';

import styles from './EncounterSchemaForm.module.scss';

type EncounterSchemaFormProps = {
  className?: string;
  schema?: EncounterSchema;
  organizationId?: string;
  importedTemplateSeriesId?: string;
  onSaveComplete?: (schemaId?: string) => void;
};

type DraftSchemaDocument = {
  templateSeriesId: string;
  inclusion: DocumentInclusion;
  fallbackCode?: string;
  fallbackName?: string;
  fallbackType?: string;
};

type FormData = {
  name: string;
  code: string;
  language: string;
  description: string;
  status: VersionStatus;
  analyticsModelSeriesId: string;
  documents: DraftSchemaDocument[];
};

const toDraftInclusion = (
  inclusion?: SchemaDocumentRef['inclusion'],
): DraftSchemaDocument['inclusion'] =>
  inclusion?.toLowerCase() === 'always' ? 'always' : 'on_demand';

const resolveTemplateSeriesId = (
  documentRef: SchemaDocumentRef,
  templateSeriesIdsByCode: Map<string, string>,
): string =>
  documentRef.template_series_id ||
  templateSeriesIdsByCode.get(documentRef.code) ||
  '';

const createEmptyFormData = (importedTemplateSeriesId?: string): FormData => ({
  name: '',
  code: '',
  language: 'en',
  description: '',
  status: 'draft',
  analyticsModelSeriesId: '',
  documents: importedTemplateSeriesId
    ? [{ templateSeriesId: importedTemplateSeriesId, inclusion: 'on_demand' }]
    : [],
});

const createFormDataFromSchema = (
  schema: EncounterSchema,
  templateSeriesIdsByCode: Map<string, string>,
): FormData => ({
  name: schema.name,
  code: schema.code,
  language: schema.language,
  description: schema.description ?? '',
  status: (schema.status as VersionStatus) ?? 'draft',
  analyticsModelSeriesId: schema.analytics_model_series_id ?? '',
  documents: schema.documents
    .map((documentRef) => ({
      templateSeriesId: resolveTemplateSeriesId(
        documentRef,
        templateSeriesIdsByCode,
      ),
      inclusion: toDraftInclusion(documentRef.inclusion),
      fallbackCode: documentRef.code,
      fallbackName: documentRef.name,
      fallbackType: documentRef.type,
    }))
    .filter((documentRef) => Boolean(documentRef.templateSeriesId)),
});

export const EncounterSchemaForm: FC<EncounterSchemaFormProps> = (props) => {
  const {
    className,
    schema,
    organizationId,
    importedTemplateSeriesId,
    onSaveComplete,
  } = props;

  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const templatesBackPath = resolveTemplatesBackPath(
    location.state,
    getPathWithSearch(location.pathname, location.search),
  );

  const documentTemplates = useAppSelector(
    templatesModel.selectors.selectDocumentTemplates,
  );

  const templateSeriesIdsByCode = useMemo(
    () =>
      new Map(
        documentTemplates
          .filter((template) => template.code && template.series_id)
          .map((template) => [template.code, template.series_id]),
      ),
    [documentTemplates],
  );

  const [formData, setFormData] = useState<FormData>(() =>
    schema
      ? createFormDataFromSchema(schema, templateSeriesIdsByCode)
      : createEmptyFormData(importedTemplateSeriesId),
  );
  const [isCodeManuallyEdited, setIsCodeManuallyEdited] = useState(
    Boolean(schema),
  );
  const [documentsError, setDocumentsError] = useState<string | undefined>();
  const [isAddDocumentDialogOpen, setIsAddDocumentDialogOpen] = useState(false);

  useEffect(() => {
    setFormData(
      schema
        ? createFormDataFromSchema(schema, templateSeriesIdsByCode)
        : createEmptyFormData(importedTemplateSeriesId),
    );
    setIsCodeManuallyEdited(Boolean(schema));
    setDocumentsError(undefined);
    setIsAddDocumentDialogOpen(false);
  }, [schema, importedTemplateSeriesId, templateSeriesIdsByCode]);

  useEffect(() => {
    if (organizationId && documentTemplates.length === 0) {
      dispatch(
        templatesModel.fetchDocumentTemplates({
          organization_id: organizationId,
        }),
      );
    }
  }, [documentTemplates.length, organizationId]);

  const languageOptions = useMemo(
    () => [
      {
        label: formatMessage(encounterSchemaMessages.languageEnglish),
        value: 'en',
      },
      {
        label: formatMessage(encounterSchemaMessages.languageRussian),
        value: 'ru',
      },
      {
        label: formatMessage(encounterSchemaMessages.languageGerman),
        value: 'de',
      },
      {
        label: formatMessage(encounterSchemaMessages.languageSpanish),
        value: 'es',
      },
      {
        label: formatMessage(encounterSchemaMessages.languagePortuguese),
        value: 'pt',
      },
    ],
    [formatMessage],
  );

  const statusOptions = useMemo(
    () => [
      {
        label: formatMessage(templateEditorMessages.statusCurrentLabel),
        value: 'active',
      },
      {
        label: formatMessage(templateEditorMessages.statusDraftLabel),
        value: 'draft',
      },
    ],
    [formatMessage],
  );

  const inclusionOptions = useMemo(
    () => [
      {
        label: formatMessage(encounterSchemaMessages.inclusionAlways),
        value: 'always',
      },
      {
        label: formatMessage(encounterSchemaMessages.inclusionOnDemand),
        value: 'on_demand',
      },
    ],
    [formatMessage],
  );

  const templateBySeriesId = useMemo(
    () =>
      new Map(
        documentTemplates
          .filter((template) => template.series_id)
          .map((template) => [template.series_id, template]),
      ),
    [documentTemplates],
  );

  const selectedTemplateSeriesIds = useMemo(
    () =>
      new Set(
        formData.documents
          .map((documentRef) => documentRef.templateSeriesId)
          .filter(Boolean),
      ),
    [formData.documents],
  );

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const name = event.target.value;

      setFormData((prev) => ({
        ...prev,
        name,
        code: isCodeManuallyEdited ? prev.code : generateCodeFromName(name),
      }));
    },
    [isCodeManuallyEdited],
  );

  const handleCodeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setIsCodeManuallyEdited(true);
      setFormData((prev) => ({ ...prev, code: event.target.value }));
    },
    [],
  );

  const handleLanguageChange = useCallback(
    (newValue: ChangeEvent | ValueType) => {
      if (typeof newValue === 'string') {
        setFormData((prev) => ({ ...prev, language: newValue }));
      }
    },
    [],
  );

  const handleStatusChange = useCallback(
    (newValue: ChangeEvent | ValueType) => {
      if (typeof newValue === 'string') {
        setFormData((prev) => ({
          ...prev,
          status: newValue as VersionStatus,
        }));
      }
    },
    [],
  );

  const handleDescriptionChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, description: event.target.value }));
    },
    [],
  );

  const getDocumentTypeForRef = useCallback(
    (documentRef: DraftSchemaDocument): DocumentType | undefined => {
      const template = templateBySeriesId.get(documentRef.templateSeriesId);
      return (template?.type ?? documentRef.fallbackType) as
        | DocumentType
        | undefined;
    },
    [templateBySeriesId],
  );

  const handleAddDocument = useCallback(
    (templateSeriesId: string) => {
      if (!templateSeriesId) {
        return;
      }

      const isDuplicate = formData.documents.some(
        (documentRef) => documentRef.templateSeriesId === templateSeriesId,
      );

      if (isDuplicate) {
        setDocumentsError(
          formatMessage(encounterSchemaMessages.duplicateDocument),
        );
        return;
      }

      setFormData((prev) => ({
        ...prev,
        documents: [
          ...prev.documents,
          {
            templateSeriesId,
            inclusion: 'on_demand',
          },
        ],
      }));
      setDocumentsError(undefined);
    },
    [formatMessage, formData.documents],
  );

  const handleOpenAddDocumentDialog = useCallback(() => {
    setIsAddDocumentDialogOpen(true);
  }, []);

  const handleCloseAddDocumentDialog = useCallback(() => {
    setIsAddDocumentDialogOpen(false);
  }, []);

  const handleDocumentInclusionChange = useCallback(
    (templateSeriesId: string, newValue: ChangeEvent | ValueType) => {
      if (typeof newValue !== 'string') {
        return;
      }

      const nextInclusion = newValue as DraftSchemaDocument['inclusion'];
      const targetRef = formData.documents.find(
        (documentRef) => documentRef.templateSeriesId === templateSeriesId,
      );

      if (!targetRef || targetRef.inclusion === nextInclusion) {
        return;
      }

      if (nextInclusion === 'always') {
        const documentType = getDocumentTypeForRef(targetRef);

        if (isProtocolTemplate(documentType)) {
          setDocumentsError(
            formatMessage(encounterSchemaMessages.protocolCannotBeAlways),
          );
          return;
        }

        const alwaysCount = formData.documents.filter(
          (documentRef) =>
            documentRef.inclusion === 'always' &&
            documentRef.templateSeriesId !== templateSeriesId,
        ).length;

        if (alwaysCount >= MAX_ALWAYS_DOCUMENTS) {
          setDocumentsError(
            formatMessage(encounterSchemaMessages.tooManyAlwaysDocuments, {
              max: MAX_ALWAYS_DOCUMENTS,
            }),
          );
          return;
        }
      }

      setDocumentsError(undefined);
      setFormData((prev) => ({
        ...prev,
        documents: prev.documents.map((documentRef) =>
          documentRef.templateSeriesId === templateSeriesId
            ? { ...documentRef, inclusion: nextInclusion }
            : documentRef,
        ),
      }));
    },
    [formData.documents, formatMessage, getDocumentTypeForRef],
  );

  const handleRemoveDocument = useCallback((templateSeriesId: string) => {
    setFormData((prev) => ({
      ...prev,
      documents: prev.documents.filter(
        (documentRef) => documentRef.templateSeriesId !== templateSeriesId,
      ),
    }));
  }, []);

  const moveDocument = useCallback(
    (templateSeriesId: string, direction: 'up' | 'down') => {
      setFormData((prev) => {
        const currentIndex = prev.documents.findIndex(
          (documentRef) => documentRef.templateSeriesId === templateSeriesId,
        );
        const nextIndex =
          direction === 'up' ? currentIndex - 1 : currentIndex + 1;

        if (
          currentIndex < 0 ||
          nextIndex < 0 ||
          nextIndex >= prev.documents.length
        ) {
          return prev;
        }

        const nextDocuments = [...prev.documents];
        const [moved] = nextDocuments.splice(currentIndex, 1);
        nextDocuments.splice(nextIndex, 0, moved);

        return {
          ...prev,
          documents: nextDocuments,
        };
      });
    },
    [],
  );

  const handleOpenTemplate = useCallback(
    (templateSeriesId: string) => {
      const template = templateBySeriesId.get(templateSeriesId);

      if (template?.id) {
        navigate(
          buildTemplatesUrl({
            editor: 'document',
            mode: 'view',
            id: template.id,
          }),
        );
      }
    },
    [navigate, templateBySeriesId],
  );

  const handleCancel = useCallback(() => {
    if (schema?.id) {
      navigate(buildTemplatesUrl({ specialty: schema.id }));
      return;
    }

    navigate(templatesBackPath);
  }, [navigate, schema?.id, templatesBackPath]);

  const handleDelete = useCallback(() => {
    if (!schema?.id) {
      return;
    }

    const schemaId = schema.id;

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.Confirm,
        data: {
          title: formatMessage(templateListMessages.deleteSpecialtyConfirm),
          description: formatMessage(
            templateListMessages.deleteSpecialtyConfirmDescription,
          ),
          okText: formatMessage(
            templateListMessages.deleteSpecialtyConfirmAction,
          ),
          danger: true,
          onConfirm: async () => {
            dispatch(modalModel.actions.closeModal(ModalID.Confirm));
            try {
              await dispatch(
                templatesModel.deleteEncounterSchemaThunk(schemaId),
              ).unwrap();
              toastCaller({
                type: 'success',
                message: formatMessage(
                  templateListMessages.deleteSpecialtySuccess,
                ),
              });
              navigate(buildTemplatesUrl({}));
            } catch (error) {
              toastCaller({
                type: 'error',
                message:
                  error instanceof Error && error.message
                    ? error.message
                    : formatMessage(templateListMessages.deleteSpecialtyError),
              });
            }
          },
        },
      }),
    );
  }, [dispatch, formatMessage, navigate, schema?.id]);

  const handleSubmit = useCallback(async () => {
    const protocolAlways = formData.documents.find(
      (documentRef) =>
        documentRef.inclusion === 'always' &&
        isProtocolTemplate(getDocumentTypeForRef(documentRef)),
    );

    if (protocolAlways) {
      const message = formatMessage(
        encounterSchemaMessages.protocolCannotBeAlways,
      );
      setDocumentsError(message);
      toastCaller({ type: 'error', message });
      return;
    }

    const alwaysCount = formData.documents.filter(
      (documentRef) => documentRef.inclusion === 'always',
    ).length;

    if (alwaysCount > MAX_ALWAYS_DOCUMENTS) {
      const message = formatMessage(
        encounterSchemaMessages.tooManyAlwaysDocuments,
        { max: MAX_ALWAYS_DOCUMENTS },
      );
      setDocumentsError(message);
      toastCaller({ type: 'error', message });
      return;
    }

    const code = formData.code.trim() || generateCodeFromName(formData.name);
    const payload = {
      code,
      language: formData.language,
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      status: formData.status,
      analytics_model_series_id: formData.analyticsModelSeriesId,
      documents: formData.documents
        .filter((documentRef) => documentRef.templateSeriesId)
        .map((documentRef) => ({
          template_series_id: documentRef.templateSeriesId,
          inclusion: documentRef.inclusion,
        })),
    };

    try {
      const savedSchema = schema?.id
        ? await dispatch(
            templatesModel.updateEncounterSchemaThunk({
              id: schema.id,
              data: payload,
            }),
          ).unwrap()
        : await dispatch(
            templatesModel.createEncounterSchemaThunk({
              ...payload,
              organization_id: organizationId ?? '',
            }),
          ).unwrap();

      toastCaller({
        type: 'success',
        message: formatMessage(encounterSchemaMessages.saveSuccess),
      });
      onSaveComplete?.(savedSchema.id);
    } catch (error) {
      toastCaller({
        type: 'error',
        message:
          error instanceof Error && error.message
            ? error.message
            : formatMessage(encounterSchemaMessages.saveError),
      });
    }
  }, [
    dispatch,
    formData,
    formatMessage,
    getDocumentTypeForRef,
    onSaveComplete,
    organizationId,
    schema,
  ]);

  const isFormValid = formData.name.trim().length > 0;

  return (
    <div
      className={cn(styles.container, className)}
      data-section="encounter-schema-form"
    >
      <div className={styles.header}>
        <h1 className={cn('h4', styles.title)}>
          {schema?.name ||
            formData.name ||
            formatMessage(templateListMessages.createSpecialty)}
        </h1>
        <div className={styles.headerActions}>
          <Select
            options={statusOptions}
            value={formData.status}
            onChange={handleStatusChange}
            className={styles.statusSelect}
          />
          {schema?.id ? (
            <Button
              variant="tertiary"
              size="medium"
              danger
              onClick={handleDelete}
            >
              {formatMessage(templateListMessages.delete)}
            </Button>
          ) : null}
          <Button variant="tertiary" size="medium" onClick={handleCancel}>
            {formatMessage(encounterSchemaMessages.cancel)}
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleSubmit}
            disabled={!isFormValid}
          >
            {formatMessage(encounterSchemaMessages.save)}
          </Button>
        </div>
      </div>

      <div className={styles.formSection}>
        <div className={styles.formRow}>
          <span className={cn('p3', styles.formLabel)}>
            {formatMessage(encounterSchemaMessages.name)}
          </span>
          <div className={styles.formField}>
            <Input
              name="name"
              value={formData.name}
              onChange={handleNameChange}
              required
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <span className={cn('p3', styles.formLabel)}>
            {formatMessage(encounterSchemaMessages.language)}
          </span>
          <div className={styles.formField}>
            <Select
              options={languageOptions}
              value={formData.language}
              onChange={handleLanguageChange}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <span className={cn('p3', styles.formLabel)}>
            {formatMessage(encounterSchemaMessages.code)}
          </span>
          <div className={styles.formField}>
            <Input
              name="code"
              value={formData.code}
              onChange={handleCodeChange}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <span className={cn('p3', styles.formLabel)}>
            {formatMessage(encounterSchemaMessages.description)}
          </span>
          <div className={styles.formField}>
            <Textarea
              value={formData.description}
              onChange={handleDescriptionChange}
              textareaClassName={styles.textarea}
              rows={3}
              isResizeDisabled
            />
          </div>
        </div>
      </div>

      <div className={styles.formSection}>
        <div className={styles.formRow}>
          <span className={cn('p3', styles.formLabel)}>
            {formatMessage(encounterSchemaMessages.documents)}
          </span>
          <div className={styles.formField}>
            <div className={styles.documentToolbar}>
              <Button
                variant="gray"
                size="medium"
                icon="plus"
                className={styles.addTemplateButton}
                onClick={handleOpenAddDocumentDialog}
              >
                {formatMessage(encounterSchemaMessages.addTemplate)}
              </Button>
            </div>

            {documentsError ? (
              <div className={cn('p3', styles.documentsError)}>
                {documentsError}
              </div>
            ) : null}

            {formData.documents.length === 0 ? (
              <div className={cn('p3', styles.emptyDocuments)}>
                {formatMessage(encounterSchemaMessages.noDocuments)}
              </div>
            ) : (
              <div className={styles.documentsList}>
                {formData.documents.map((documentRef, index) => {
                  const template = templateBySeriesId.get(
                    documentRef.templateSeriesId,
                  );
                  const templateName =
                    template?.name ||
                    documentRef.fallbackName ||
                    documentRef.templateSeriesId;
                  const templateCode =
                    template?.code || documentRef.fallbackCode || '';
                  const templateType =
                    template?.type || documentRef.fallbackType || '';

                  return (
                    <div
                      key={documentRef.templateSeriesId}
                      className={styles.documentCard}
                    >
                      <div className={styles.documentInfo}>
                        <span className={cn('h7', styles.documentName)}>
                          {templateName}
                        </span>
                        <div className={styles.documentMeta}>
                          {templateCode ? (
                            <span className={cn('p4b', styles.documentBadge)}>
                              {templateCode}
                            </span>
                          ) : null}
                          {templateType ? (
                            <span className={cn('p4b', styles.documentBadge)}>
                              {templateType}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div className={styles.documentActions}>
                        <Select
                          options={inclusionOptions}
                          value={documentRef.inclusion}
                          onChange={(newValue) =>
                            handleDocumentInclusionChange(
                              documentRef.templateSeriesId,
                              newValue,
                            )
                          }
                          className={styles.inclusionSelect}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 1000,
                            }),
                          }}
                        />
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() =>
                            handleOpenTemplate(documentRef.templateSeriesId)
                          }
                          disabled={!template?.id}
                        >
                          {formatMessage(encounterSchemaMessages.openTemplate)}
                        </Button>
                        <Button
                          variant="tertiary"
                          size="small"
                          onClick={() =>
                            moveDocument(documentRef.templateSeriesId, 'up')
                          }
                          disabled={index === 0}
                        >
                          {formatMessage(encounterSchemaMessages.moveUp)}
                        </Button>
                        <Button
                          variant="tertiary"
                          size="small"
                          onClick={() =>
                            moveDocument(documentRef.templateSeriesId, 'down')
                          }
                          disabled={index === formData.documents.length - 1}
                        >
                          {formatMessage(encounterSchemaMessages.moveDown)}
                        </Button>
                        <Button
                          variant="tertiary"
                          size="small"
                          danger
                          onClick={() =>
                            handleRemoveDocument(documentRef.templateSeriesId)
                          }
                        >
                          {formatMessage(
                            encounterSchemaMessages.removeDocument,
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <AddSchemaDocumentDialog
        isOpen={isAddDocumentDialogOpen}
        language={formData.language}
        attachedTemplateSeriesIds={selectedTemplateSeriesIds}
        organizationId={organizationId}
        onSelect={handleAddDocument}
        onClose={handleCloseAddDocumentDialog}
      />
    </div>
  );
};
